import React, { useState, useEffect } from 'react';
import { workOrderService } from '../../services/workOrder/workOrderService';
import { WorkOrder } from '../../types/workOrder';
import { WorkOrderTable } from '../../components/workOrder/WorkOrderTable';
import { CreateWorkOrderModal } from '../../components/workOrder/CreateWorkOrderModal';
import { Button } from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/Feedback';
import { Plus, Search, Filter, Wrench } from 'lucide-react';

export const WorkOrderListPage: React.FC<{ onSelectWorkOrder: (wo: WorkOrder) => void }> = ({ onSelectWorkOrder }) => {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchWorkOrders = async () => {
    setIsLoading(true);
    const res = await workOrderService.getWorkOrders({ search, status: statusFilter });
    if (res.success) {
      setWorkOrders(res.data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchWorkOrders();
  }, [search, statusFilter]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Work Order Registry</h2>
          <p className="text-xs text-slate-400">Issue, track and execute work orders linked to parent complaints</p>
        </div>
        <Button variant="primary" icon={<Plus className="w-4 h-4" />} onClick={() => setIsModalOpen(true)}>
          Issue New Work Order
        </Button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search WO ID, scope..."
            className="w-full bg-slate-950 border border-slate-700 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-200"
          >
            <option value="ALL">All Work Order Statuses</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="IN PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="INSPECTED">Inspected</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner size="lg" text="Loading Work Orders..." />
      ) : (
        <WorkOrderTable workOrders={workOrders} onSelect={onSelectWorkOrder} />
      )}

      <CreateWorkOrderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchWorkOrders}
      />
    </div>
  );
};
